import { useState, useEffect, useRef } from "react";
import "./ImageMarkingTool.css";
import svg from "../../assets/images/crosshairs-svgrepo-com.svg";

const FINGERS = ["Index", "Middle", "Ring", "Pinky"];
function BoundingBox({ box, boxKey, onMouseDown, onLineMouseDown }) {
  if (!box) return null;
  return (
    <div
      className="bounding-box-container"
      style={{
        transform: `translate(${box.x}px, ${box.y}px)`,
        width: box.width,
        height: box.height,
      }}
    >
      <div
        className="bounding-box-visual"
        data-type="move"
        data-key={boxKey}
        onMouseDown={onMouseDown}
        style={{ transform: `rotate(${box.rotation}deg)` }}
      >
        {/* === START OF MODIFICATION === */}
        {box.hasOwnProperty("lineY") && (
          // This container group handles vertical positioning for the line and its points
          <div className="line-container" style={{ top: `${box.lineY}%` }}>
            {/* The draggable area for the line. It's invisible but captures mouse events. */}
            <div
              className="handle handle-line"
              data-type="drag-line"
              data-key={boxKey}
              onMouseDown={onLineMouseDown}
            ></div>

            {/* The visual point/handle on the LEFT side */}
            <div className="handle line-point line-point-left"></div>

            {/* The visual point/handle on the RIGHT side */}
            <div className="handle line-point line-point-right"></div>
          </div>
        )}
        {/* === END OF MODIFICATION === */}

        <div
          className="handle handle-rotate"
          data-type="rotate"
          data-key={boxKey}
          onMouseDown={onMouseDown}
        ></div>
        <div
          className="handle handle-top-left"
          data-type="resize-top-left"
          data-key={boxKey}
          onMouseDown={onMouseDown}
        ></div>
        <div
          className="handle handle-top-right"
          data-type="resize-top-right"
          data-key={boxKey}
          onMouseDown={onMouseDown}
        ></div>
        <div
          className="handle handle-bottom-left"
          data-type="resize-bottom-left"
          data-key={boxKey}
          onMouseDown={onMouseDown}
        ></div>
        <div
          className="handle handle-bottom-right"
          data-type="resize-bottom-right"
          data-key={boxKey}
          onMouseDown={onMouseDown}
        ></div>
      </div>
    </div>
  );
}

function CustomCrosshair({ position }) {
  if (!position) return null;
  return (
    <div
      className="custom-crosshair"
      style={{ left: `${position.x}px`, top: `${position.y}px` }}
    >
      <img src={svg} className="h-4 w-4" />
      {/* <div className="cross-h"></div>
      <div className="cross-v"></div> */}
    </div>
  );
}

function ZoomPane({ imageUrl, position, imageDimensions }) {
  if (!imageDimensions) return null;

  const zoomLevel = 3;
  const paneDimensions = { width: 200, height: 200 };

  const bgSize = {
    width: imageDimensions.width * zoomLevel,
    height: imageDimensions.height * zoomLevel,
  };

  const bgPos = {
    x: -(position.x * bgSize.width - paneDimensions.width / 2),
    y: -(position.y * bgSize.height - paneDimensions.height / 2),
  };

  const style = {
    backgroundImage: `url(${imageUrl})`,
    backgroundSize: `${bgSize.width}px ${bgSize.height}px`,
    backgroundPosition: `${bgPos.x}px ${bgPos.y}px`,
  };

  return <div className="zoom-pane" style={style}></div>;
}

// --- Main Application Component ---

function ImageMarkingTool({
  iBodyTypeId,
  imageUrl,
  isPythonCalled,
  getPythonCoordaintes,
}) {
  const [image, setImage] = useState(imageUrl);
  const [mainSelection, setMainSelection] = useState(iBodyTypeId);
  const [points, setPoints] = useState({});
  const [boxes, setBoxes] = useState({});
  const [activeBoxKey, setActiveBoxKey] = useState(null);
  const [currentMarkingTask, setCurrentMarkingTask] = useState(null);
  const [earSelection, setEarSelection] = useState("Single");
  const [mousePosition, setMousePosition] = useState(null);
  const [zoomData, setZoomData] = useState(null);
  const [imageDimensions, setImageDimensions] = useState(null);

  const imagePanelRef = useRef(null);
  const imageRef = useRef(null);
  const interactionRef = useRef({});

  useEffect(() => {
    setCurrentMarkingTask(null);
    setBoxes({});
    setActiveBoxKey(null);
    setPoints({});
    setEarSelection("Single");
    if (mainSelection === 1) {
      // Wrist
      const wristKey = "Wrist";
      setBoxes({
        [wristKey]: {
          x: 50,
          y: 50,
          width: 150,
          height: 150,
          rotation: 0,
          lineY: 50,
        },
      });
      setPoints({ [iBodyTypeId]: {} });
      setActiveBoxKey(wristKey);
    } else if (mainSelection === 3) {
      // Fingers - Start with nothing
      setPoints({ [iBodyTypeId]: {} });
      setActiveBoxKey(null);
    } else if (mainSelection === 2) {
      setPoints({ [iBodyTypeId]: { ear: [] } });
    } else if (mainSelection === 4) {
      setPoints({ [iBodyTypeId]: { left_point: [], right_point: [] } });
    }
  }, [mainSelection, iBodyTypeId]);

  useEffect(() => {
    updatePointsFromBoxes();
  }, [boxes]);

  const updatePointsFromBoxes = () => {
    if (Object.keys(boxes).length === 0 || !imageRef.current) return;

    const imageRect = imageRef.current.getBoundingClientRect();
    const normalize = (val, max) =>
      Math.max(0, Math.min(1, parseFloat(val) / max)).toFixed(4);

    const newPointsData = {};
    for (const [key, box] of Object.entries(boxes)) {
      if (!box) continue;
      const { x, y, width, height, rotation } = box;
      const centerX = x + width / 2;
      const centerY = y + height / 2;
      const rad = (rotation * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);

      const getRotatedPoint = (px, py) => {
        const translatedX = px - centerX;
        const translatedY = py - centerY;
        const rotatedX = translatedX * cos - translatedY * sin;
        const rotatedY = translatedX * sin + translatedY * cos;
        return [
          normalize(rotatedX + centerX, imageRect.width),
          normalize(rotatedY + centerY, imageRect.height),
        ];
      };

      newPointsData[key] = {
        topLeft: getRotatedPoint(x, y),
        topRight: getRotatedPoint(x + width, y),
        bottomLeft: getRotatedPoint(x, y + height),
        bottomRight: getRotatedPoint(x + width, y + height),
        rotation: rotation.toFixed(2),
      };

      if (key === "Wrist" && box.hasOwnProperty("lineY")) {
        newPointsData[key].center = [
          normalize(centerX, imageRect.width),
          normalize(centerY, imageRect.height),
        ];

        const lineYPos = y + (height * box.lineY) / 100;
        newPointsData[key].centerLeft = getRotatedPoint(x, lineYPos);
        newPointsData[key].centerRight = getRotatedPoint(x + width, lineYPos);
      }
    }

    setPoints((prev) => ({ ...prev, [iBodyTypeId]: newPointsData }));
  };

  const handleImageClick = (e) => {
    if (!currentMarkingTask) return;
    const normalizedPoint = getNormalizedCoords(e);
    if (!normalizedPoint) return;
    const { part, subPart, index } = currentMarkingTask;
    if (part === 2) {
      setPoints((prev) => {
        const newPoints = { ...prev };
        const earPoints = [...(newPoints[iBodyTypeId]?.ear || [])];
        earPoints[index] = normalizedPoint;
        newPoints[iBodyTypeId] = { ...newPoints[iBodyTypeId], ear: earPoints };
        return newPoints;
      });
      setCurrentMarkingTask(null);
    } else if (part === 4) {
      setPoints((prev) => ({
        ...prev,
        [iBodyTypeId]: { ...prev[iBodyTypeId], [subPart]: [normalizedPoint] },
      }));
      setCurrentMarkingTask(null);
    }
  };

  const getNormalizedCoords = (e) => {
    if (!imageRef.current || !imagePanelRef.current) return null;
    const imageRect = imageRef.current.getBoundingClientRect();
    const x = e.clientX - imageRect.left;
    const y = e.clientY - imageRect.top;
    const normalizedX = x / imageRect.width;
    const normalizedY = y / imageRect.height;
    if (
      normalizedX < 0 ||
      normalizedX > 1 ||
      normalizedY < 0 ||
      normalizedY > 1
    )
      return null;
    return [
      parseFloat(normalizedX.toFixed(4)),
      parseFloat(normalizedY.toFixed(4)),
    ];
  };

  const onMouseDown = (e) => {
    e.stopPropagation();
    e.preventDefault();

    const { type, key } = e.target.dataset;
    if (!type || !key || !boxes[key]) return;

    setActiveBoxKey(key);

    const imageRect = imageRef.current.getBoundingClientRect();
    const startX = e.clientX - imageRect.left;
    const startY = e.clientY - imageRect.top;

    const box = boxes[key];
    const boxCenter = { x: box.x + box.width / 2, y: box.y + box.height / 2 };
    let anchorPoint;

    // NEW: Handle line dragging separately
    if (type === "drag-line") {
      interactionRef.current = {
        type,
        key,
        startY,
        initialBox: { ...box },
      };
    } else {
      if (type.includes("resize")) {
        const rad = (box.rotation * Math.PI) / 180;
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        const getRotatedCorner = (cx, cy, w, h, corner) => {
          const halfW = w / 2;
          const halfH = h / 2;
          let p = {};
          if (corner.includes("top")) p.y = -halfH;
          if (corner.includes("bottom")) p.y = halfH;
          if (corner.includes("left")) p.x = -halfW;
          if (corner.includes("right")) p.x = halfW;
          return {
            x: cx + (p.x * cos - p.y * sin),
            y: cy + (p.x * sin + p.y * cos),
          };
        };
        if (type.includes("bottom-right"))
          anchorPoint = getRotatedCorner(
            boxCenter.x,
            boxCenter.y,
            box.width,
            box.height,
            "top-left"
          );
        else if (type.includes("bottom-left"))
          anchorPoint = getRotatedCorner(
            boxCenter.x,
            boxCenter.y,
            box.width,
            box.height,
            "top-right"
          );
        else if (type.includes("top-right"))
          anchorPoint = getRotatedCorner(
            boxCenter.x,
            boxCenter.y,
            box.width,
            box.height,
            "bottom-left"
          );
        else if (type.includes("top-left"))
          anchorPoint = getRotatedCorner(
            boxCenter.x,
            boxCenter.y,
            box.width,
            box.height,
            "bottom-right"
          );
      }

      interactionRef.current = {
        type,
        key,
        startX,
        startY,
        initialBox: { ...box },
        boxCenter,
        anchorPoint,
      };
    }
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  };

  const onMouseMove = (e) => {
    const { type, key, initialBox, boxCenter, anchorPoint } =
      interactionRef.current;
    if (!type || !key || !imageRef.current) return;

    const imageRect = imageRef.current.getBoundingClientRect();
    const mouseX = e.clientX - imageRect.left;
    const mouseY = e.clientY - imageRect.top;

    let newBox = { ...boxes[key] };

    if (type === "move") {
      const dx = mouseX - interactionRef.current.startX;
      const dy = mouseY - interactionRef.current.startY;
      newBox.x = initialBox.x + dx;
      newBox.y = initialBox.y + dy;
    } else if (type === "rotate") {
      const angle = Math.atan2(mouseY - boxCenter.y, mouseX - boxCenter.x);
      newBox.rotation = (angle * 180) / Math.PI + 90;
    } else if (type.includes("resize")) {
      const currentVector = {
        x: mouseX - anchorPoint.x,
        y: mouseY - anchorPoint.y,
      };
      const rad = (initialBox.rotation * Math.PI) / 180;
      const cos = Math.cos(-rad);
      const sin = Math.sin(-rad);
      const unrotatedVector = {
        x: currentVector.x * cos - currentVector.y * sin,
        y: currentVector.x * sin + currentVector.y * cos,
      };
      let newWidth = Math.max(Math.abs(unrotatedVector.x), 20);
      let newHeight = Math.max(Math.abs(unrotatedVector.y), 20);
      const newCenter = {
        x: anchorPoint.x + currentVector.x / 2,
        y: anchorPoint.y + currentVector.y / 2,
      };
      newBox.width = newWidth;
      newBox.height = newHeight;
      newBox.x = newCenter.x - newWidth / 2;
      newBox.y = newCenter.y - newHeight / 2;
    }
    // NEW: Handle line dragging logic
    else if (type === "drag-line") {
      const rad = (newBox.rotation * Math.PI) / 180;
      const cos = Math.cos(-rad);
      const sin = Math.sin(-rad);

      // Translate mouse position into the box's unrotated coordinate system
      const localMouseX = mouseX - newBox.x;
      const localMouseY = mouseY - newBox.y;

      const unrotatedMouseX = localMouseX * cos - localMouseY * sin;
      const unrotatedMouseY = localMouseX * sin + localMouseY * cos;

      // Calculate the line's Y position as a percentage and clamp it
      const newY = (unrotatedMouseY / newBox.height) * 100;
      newBox.lineY = Math.max(0, Math.min(100, newY));
    }

    setBoxes((prev) => ({ ...prev, [key]: newBox }));
  };

  const onMouseUp = () => {
    interactionRef.current = {};
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onMouseUp);
  };

  const handleFingerSelect = (finger) => {
    setActiveBoxKey(finger);
    if (!boxes[finger]) {
      setBoxes((prev) => ({
        ...prev,
        [finger]: { x: 100, y: 100, width: 30, height: 100, rotation: 0 },
      }));
    }
  };
  const handleEarSelectionChange = (e) => {
    setEarSelection(e.target.value);
    setPoints((prev) => ({
      ...prev,
      [iBodyTypeId]: { ...prev[iBodyTypeId], ear: [] },
    }));
    setCurrentMarkingTask(null);
  };
  const renderSubControls = () => {
    switch (mainSelection) {
      case 3:
        return (
          <div className="sub-controls">
            {" "}
            <strong>Select Finger to Mark:</strong>{" "}
            <div className="flex gap-4">
              {" "}
              {FINGERS.map((finger) => (
                <button
                  key={finger}
                  className={activeBoxKey === finger ? "active" : ""}
                  onClick={() => handleFingerSelect(finger)}
                >
                  {" "}
                  {finger} {points[iBodyTypeId]?.[finger] && "✔"}{" "}
                </button>
              ))}{" "}
            </div>{" "}
          </div>
        );
      case 1:
        return (
          <p>
            Adjust the box to fit the Wrist. Points are saved automatically.
          </p>
        );
      case 2: {
        const earPoints = points[iBodyTypeId]?.ear || [];
        return (
          <div className="sub-controls">
            {" "}
            <label htmlFor="ear-select">Select Ear Type:</label>{" "}
            <select
              id="ear-select"
              value={earSelection}
              onChange={handleEarSelectionChange}
            >
              {" "}
              <option value="Single">Single Ear</option>{" "}
              <option value="Both">Both Ears</option>{" "}
            </select>{" "}
            <div className="flex flex-col gap-2 mt-2">
              {" "}
              {earSelection === "Single" && (
                <button
                  onClick={() => setCurrentMarkingTask({ part: 2, index: 0 })}
                  className={`${earPoints[0] ? "completed" : ""} ${
                    currentMarkingTask?.index === 0 ? "active" : ""
                  }`}
                >
                  {" "}
                  Mark Ear {earPoints[0] && "✔"}{" "}
                </button>
              )}{" "}
              {earSelection === "Both" && (
                <>
                  {" "}
                  <button
                    onClick={() => setCurrentMarkingTask({ part: 2, index: 0 })}
                    className={`${earPoints[0] ? "completed" : ""} ${
                      currentMarkingTask?.index === 0 ? "active" : ""
                    }`}
                  >
                    {" "}
                    Mark Left Ear {earPoints[0] && "✔"}{" "}
                  </button>{" "}
                  <button
                    onClick={() => setCurrentMarkingTask({ part: 2, index: 1 })}
                    className={`${earPoints[1] ? "completed" : ""} ${
                      currentMarkingTask?.index === 1 ? "active" : ""
                    }`}
                  >
                    {" "}
                    Mark Right Ear {earPoints[1] && "✔"}{" "}
                  </button>{" "}
                </>
              )}{" "}
            </div>{" "}
          </div>
        );
      }
      case 4: {
        const leftDone = (points[iBodyTypeId]?.left_point?.length || 0) > 0;
        const rightDone = (points[iBodyTypeId]?.right_point?.length || 0) > 0;
        return (
          <div className="sub-controls">
            {" "}
            <strong>Mark Neck Points (Editable):</strong>{" "}
            <div className="flex gap-4">
              {" "}
              <button
                onClick={() =>
                  setCurrentMarkingTask({ part: 4, subPart: "left_point" })
                }
                className={`${leftDone ? "completed" : ""} ${
                  currentMarkingTask?.subPart === "left_point" ? "active" : ""
                }`}
              >
                {" "}
                Mark Left Point {leftDone && "✔"}{" "}
              </button>{" "}
              <button
                onClick={() =>
                  setCurrentMarkingTask({ part: 4, subPart: "right_point" })
                }
                className={`${rightDone ? "completed" : ""} ${
                  currentMarkingTask?.subPart === "right_point" ? "active" : ""
                }`}
              >
                {" "}
                Mark Right Point {rightDone && "✔"}{" "}
              </button>{" "}
            </div>{" "}
          </div>
        );
      }
      default:
        return null;
    }
  };

  const getTaskMessage = () => {
    if (currentMarkingTask) {
      const { part, subPart, index } = currentMarkingTask;
      if (part === 4) return `Click to mark the ${subPart?.replace("_", " ")}.`;
      if (part === 2) {
        if (earSelection === "Both") {
          return `Click to mark the ${index === 0 ? "Left Ear" : "Right Ear"}.`;
        }
        return "Click to mark the Ear.";
      }
    }
    if ((mainSelection === 1 || mainSelection === 3) && activeBoxKey) {
      return `Adjusting ${activeBoxKey}. Drag, resize, or rotate the box.`;
    }
    if (mainSelection === 3 && !activeBoxKey) {
      return "Select a finger from the panel to begin marking.";
    }
    return "Select a task from the panel to begin.";
  };

  const getOverlayStyle = () => {
    if (!imageRef.current || !imagePanelRef.current) return {};
    const imageRect = imageRef.current.getBoundingClientRect();
    const panelRect = imagePanelRef.current.getBoundingClientRect();
    return {
      position: "absolute",
      left: imageRect.left - panelRect.left,
      top: imageRect.top - panelRect.top,
      width: imageRect.width,
      height: imageRect.height,
      cursor: currentMarkingTask ? "none" : "default",
    };
  };

  // MODIFIED: This function now also controls the zoom pane
  const handlePanelMouseMove = (e) => {
    // Logic for the crosshair (when a marking task is active)
    if (currentMarkingTask) {
      const panelRect = imagePanelRef.current.getBoundingClientRect();
      const x = e.clientX - panelRect.left;
      const y = e.clientY - panelRect.top;
      setMousePosition({ x, y });
    }

    // NEW: Logic for the real-time zoom pane
    if (mainSelection === 2 || mainSelection === 4) {
      const normalizedPoint = getNormalizedCoords(e);
      if (normalizedPoint) {
        setZoomData({ x: normalizedPoint[0], y: normalizedPoint[1] });
      } else {
        // If mouse is over the panel but outside the image, hide zoom
        setZoomData(null);
      }
    }
  };

  // MODIFIED: This function now also hides the zoom pane
  const handlePanelMouseLeave = () => {
    setMousePosition(null); // for crosshair
    setZoomData(null); // for zoom pane
  };

  const onImageLoad = (e) => {
    setImageDimensions({
      width: e.target.naturalWidth,
      height: e.target.naturalHeight,
    });
  };

  return (
    <>
      <div className="annotator-app">
        {zoomData && (
          <ZoomPane
            imageUrl={image}
            position={zoomData}
            imageDimensions={imageDimensions}
          />
        )}

        <div className="main-layout">
          <aside className="sidebar">
            <div className="control-panel">{renderSubControls()}</div>
            <div className="task-panel">
              <p>{getTaskMessage()}</p>
            </div>
          </aside>
          <main
            ref={imagePanelRef}
            className="image-panel"
            onClick={handleImageClick}
            onMouseMove={handlePanelMouseMove}
            onMouseLeave={handlePanelMouseLeave}
          >
            <img
              ref={imageRef}
              src={image}
              alt="for annotation"
              onLoad={onImageLoad}
            />

            {imageRef.current && (
              <div className="overlay" style={getOverlayStyle()}>
                {activeBoxKey && boxes[activeBoxKey] && (
                  <BoundingBox
                    key={activeBoxKey}
                    box={boxes[activeBoxKey]}
                    boxKey={activeBoxKey}
                    onMouseDown={onMouseDown}
                    onLineMouseDown={onMouseDown} // Pass the same handler for the line
                  />
                )}
                {/* REMOVED onMouseEnter/onMouseLeave from markers */}
                {mainSelection === 2 &&
                  points[mainSelection]?.ear?.map((p, i) => {
                    if (!p) return null;
                    const isHighlighted =
                      currentMarkingTask?.part === 2 &&
                      currentMarkingTask?.index === i;
                    return (
                      <div
                        key={`ear-${i}`}
                        className={`marker ${
                          isHighlighted ? "highlighted" : ""
                        }`}
                        style={{
                          left: `${p[0] * 100}%`,
                          top: `${p[1] * 100}%`,
                          backgroundColor: "green",
                        }}
                      />
                    );
                  })}
                {mainSelection === 4 &&
                  Object.entries(points[mainSelection] || {}).flatMap(
                    ([pointKey, pointData]) => {
                      if (!pointData || !Array.isArray(pointData)) return [];
                      return pointData.map((p, i) => {
                        const isHighlighted =
                          currentMarkingTask?.part === 4 &&
                          currentMarkingTask?.subPart === pointKey;
                        return (
                          <div
                            key={`${pointKey}-${i}`}
                            className={`marker ${
                              isHighlighted ? "highlighted" : ""
                            }`}
                            style={{
                              left: `${p[0] * 100}%`,
                              top: `${p[1] * 100}%`,
                              backgroundColor: "purple",
                            }}
                          />
                        );
                      });
                    }
                  )}
              </div>
            )}
            <CustomCrosshair position={mousePosition} />
          </main>
        </div>

        <div>
          <div className="results-panel">
            <h3>Collected Points</h3>
            <pre>
              {JSON.stringify(
                points[mainSelection]
                  ? { [mainSelection]: points[mainSelection] }
                  : {},
                null,
                2
              )}
            </pre>
          </div>
          <div className="flex items-end justify-end px-4 py-1">
            {isPythonCalled ? (
              <button
                className={`py-1 px-3 text-white rounded-lg edit`}
                disabled
              >
                Save Coordinates
              </button>
            ) : (
              <button
                className={`py-1 px-3 text-white rounded-lg edit`}
                onClick={() => getPythonCoordaintes(points[iBodyTypeId])}
              >
                Get Python Coordinates
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default ImageMarkingTool;
